import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArchiveRestore,
  ArrowLeft,
  Calendar,
  Check,
  ChevronDown,
  // ⚠️ `Loader2` ET NON `RefreshCw` POUR LES CHARGEMENTS.
  // Cet écran faisait tourner l'icône aux flèches circulaires — celle du geste
  // « rafraîchir » — là où le reste de l'application emploie `Loader2`. Mesuré le
  // 05/10/2026 : `Loader2` dans 69 fichiers, `RefreshCw` dans 36. Deux écrans
  // voisins du même module ne se ressemblaient même pas : les radiés faisaient
  // tourner une icône, la liste des élèves n'affichait qu'un texte.
  //
  // ⚠️ `ArchiveRestore` ET NON `RotateCcw` SUR LES BOUTONS DE RESTAURATION.
  // J'avais d'abord gardé `RotateCcw` en écrivant qu'« elle dit bien ce qu'elle
  // fait ». C'était faux : la flèche circulaire se lit comme un chargement même à
  // l'arrêt, et au clic elle se métamorphosait en `Loader2` — l'utilisateur voyait
  // deux formes différentes au lieu d'un état qui change. Signalé deux fois en
  // recette, le 05/10/2026.
  //
  // `ArchiveRestore` ne contient aucun arc (un carton et une flèche droite), et
  // c'est déjà l'icône du bouton « Restaurer » de la messagerie
  // (layouts/MessageForm.jsx) : on s'aligne sur la convention, on n'en invente
  // pas une quatrième. `RotateCcw` garde ailleurs son sens — « Réinitialiser »,
  // « Réactiver un créneau », « Rejouer » — et ne doit pas y être touchée.
  Loader2,
  Search,
  TrendingDown,
  Users
} from 'lucide-react';
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { Toast } from '../components/ui/Toast';
import { LongToast, shouldUseLongToast } from '../components/ui/LongToast';
import ProtectedComponent from '../components/ProtectedComponent';
import { Button } from '../components/ui/button';
import ConfirmDialog from '../components/ConfirmDialog';

const ITEMS_PER_PAGE = 10;

const formatDate = (value) => {
  if (!value) return 'Date non renseignée';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date non renseignée';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatTime = (value, fallback = null) => {
  const source = value || fallback;
  if (!source) return 'Heure non renseignée';
  const date = new Date(source);
  if (Number.isNaN(date.getTime())) return 'Heure non renseignée';
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatMoney = (value) => `${Math.round(Number(value || 0)).toLocaleString('fr-FR')} FCFA`;

const getMonthsCountInclusive = (startValue, endValue) => {
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;

  const months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth() + 1;
  return Math.max(1, months);
};

const getMonthKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthLabel = (key) => {
  if (!key) return 'Non déterminé';
  const [year, month] = key.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const SimpleDropdown = ({ value, options, placeholder, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value || ''));

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 text-sm font-semibold transition-colors ${value ? 'border-[#246990]/30 text-[#246990]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
      >
        <span className="truncate">{selected?.label || placeholder}</span>
        <ChevronDown size={16} className={`ml-2 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-2 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl">
          {options.map((option) => {
            const isSelected = String(option.value) === String(value || '');

            return (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#246990]/10 font-semibold text-[#246990]' : 'text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <Check size={15} className="shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, detail, tone = 'blue' }) => {
  const styles = {
    blue: 'from-[#246990] to-[#3084b5]',
    teal: 'from-[#319190] to-[#4aa9a8]',
    red: 'from-[#c66d59] to-[#d98270]',
    slate: 'from-[#475569] to-[#64748b]'
  };

  return (
    <div className={`overflow-hidden rounded-lg bg-gradient-to-r ${styles[tone]} text-white shadow-sm`}>
      <div className="flex items-start justify-between gap-4 p-4">
        <div>
          <p className="text-sm font-medium text-white/85">{label}</p>
          <p className="mt-2 text-3xl font-bold leading-none">{value}</p>
          {detail && <p className="mt-2 text-xs text-white/80">{detail}</p>}
        </div>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/15">
          <Icon size={22} />
        </span>
      </div>
    </div>
  );
};

const ElevesRadies = () => {
  // Remettre un élève « dans sa famille » relève du module familles nombreuses.
  // Sans lui, la restauration reste individuelle.
  const { estActive } = useFeatures();
  const famillesOuvertes = estActive('module.familles');
  // Sans comptabilite, restaurer un eleve ne recompose aucun attendu : le modal
  // ne doit plus parler de paiements, de reductions ni de mois d'effet.
  const comptabiliteOuverte = estActive('module.comptabilite');

  const [elevesRadies, setElevesRadies] = useState([]);
  // Les demandes de RESTAURATION en attente, indexées par identifiant d'élève.
  // Miroir exact du repère posé sur la liste des élèves pour les radiations :
  // demander une restauration ne retire pas l'élève de cette liste — il reste
  // radié tant que la demande n'est pas approuvée — et sans marqueur, la ligne est
  // identique à celle d'un radié que personne n'a touché.
  const [restaurationsEnAttente, setRestaurationsEnAttente] = useState({});
  const [totalElevesActifs, setTotalElevesActifs] = useState(0);
  const [activeYear, setActiveYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmRestore, setConfirmRestore] = useState({ open: false, ids: [] });
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreMode, setRestoreMode] = useState('individuel');
  const [restoreTarifMode, setRestoreTarifMode] = useState('dernier_etat');
  const [restoreTargetId, setRestoreTargetId] = useState(null);
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false);
  const [restorePreviewLoadingId, setRestorePreviewLoadingId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  // Les options du message courant — `long`, `title`, `persistent`. C'est ce
  // qui permet à `shouldUseLongToast` de trancher autrement que sur la seule
  // longueur du texte.
  const [toastOptions, setToastOptions] = useState({});

  // ⚠️ UN TROISIÈME ARGUMENT `options`, SANS QUOI RIEN N'EST POSSIBLE.
  // Ces aiguilleurs ne prenaient que le message : il n'existait aucun chemin
  // pour demander le toast long, même en le voulant. Les options voyagent
  // maintenant jusqu'à `shouldUseLongToast`, qui les lit en 3e argument.
  // `{ long: true, title: … }` est donc désormais honoré par cet écran.
  const showLoadingToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('loading');
    setToastOptions(options);
    setShowToast(true);
  };

  const showSuccessToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('success');
    setToastOptions(options);
    setShowToast(true);
  };

  const showErrorToast = (message, options = {}) => {
    setToastMessage(message);
    setToastType('error');
    setToastOptions(options);
    setShowToast(true);
  };

  const loadElevesRadies = async () => {
    try {
      setLoading(true);
      const activeYearResponse = await api.anneesScolaires.getActive();
      const year = activeYearResponse?.success ? activeYearResponse.data : null;

      if (!year?.id) {
        throw new Error("Aucune année scolaire active n'est définie");
      }

      setActiveYear(year);
      const [elevesData, elevesActifs] = await Promise.all([
        api.eleves.getRadies(year.id),
        api.eleves.getAll(year.id)
      ]);
      setElevesRadies(Array.isArray(elevesData) ? elevesData : []);
      setTotalElevesActifs(Array.isArray(elevesActifs) ? elevesActifs.length : 0);
      setSelectedIds([]);
      setCurrentPage(1);

      // ⚠️ Un échec ici ne doit PAS empêcher la liste des radiés de s'afficher :
      // on perd le repère, pas les élèves.
      try {
        const attente = await api.eleves.getDemandesEnAttente();
        // La route rend les deux types ; cet écran ne regarde que les restaurations.
        setRestaurationsEnAttente(attente?.data?.restauration || {});
      } catch (erreurAttente) {
        console.warn('Demandes de restauration en attente non chargées :', erreurAttente?.message);
        setRestaurationsEnAttente({});
      }
    } catch (error) {
      console.error('Erreur chargement élèves radiés:', error);
      showErrorToast("Erreur lors du chargement des élèves radiés");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadElevesRadies();
  }, []);

  const stats = useMemo(() => {
    const totalRadies = elevesRadies.length;
    const effectifReference = totalElevesActifs + totalRadies;
    const tauxRadiation = effectifReference > 0 ? (totalRadies / effectifReference) * 100 : 0;

    const byReason = new Map();
    const byMonth = new Map();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const currentMonthKey = getMonthKey(now);
    let currentQuarterCount = 0;
    const academicStart = activeYear?.date_debut ? new Date(activeYear.date_debut) : null;
    const academicEnd = activeYear?.date_fin ? new Date(activeYear.date_fin) : now;
    const averageEnd = academicEnd && academicEnd < now ? academicEnd : now;
    const monthsInAcademicPeriod = academicStart
      ? getMonthsCountInclusive(academicStart, averageEnd)
      : Math.max(1, byMonth.size);

    elevesRadies.forEach((eleve) => {
      const reason = eleve.radiation_raison || 'Non spécifiée';
      byReason.set(reason, (byReason.get(reason) || 0) + 1);

      const monthKey = getMonthKey(eleve.date_radiation);
      if (monthKey) byMonth.set(monthKey, (byMonth.get(monthKey) || 0) + 1);

      const date = new Date(eleve.date_radiation);
      if (!Number.isNaN(date.getTime()) && date.getFullYear() === currentYear) {
        if (Math.floor(date.getMonth() / 3) === currentQuarter) {
          currentQuarterCount += 1;
        }
      }
    });

    const reasonStats = Array.from(byReason.entries())
      .map(([reason, count]) => ({
        reason,
        count,
        percentage: totalRadies > 0 ? (count / totalRadies) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count);

    const monthStats = Array.from(byMonth.entries())
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => b.count - a.count);

    const currentMonthCount = currentMonthKey ? byMonth.get(currentMonthKey) || 0 : 0;
    const monthlyAverage = totalRadies / monthsInAcademicPeriod;
    const topReason = reasonStats[0] || null;
    const topMonth = monthStats[0] || null;

    return {
      totalRadies,
      effectifReference,
      tauxRadiation,
      currentMonthCount,
      currentQuarterCount,
      activeCount: totalElevesActifs,
      monthlyAverage,
      topReason,
      topMonth,
      reasonStats,
      monthStats
    };
  }, [activeYear, elevesRadies, totalElevesActifs]);

  const reasonOptions = useMemo(() => [
    { value: '', label: 'Toutes les raisons' },
    ...stats.reasonStats.map((item) => ({ value: item.reason, label: item.reason }))
  ], [stats.reasonStats]);

  const filteredEleves = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(now.getMonth() / 3);

    return elevesRadies.filter((eleve) => {
      const searchable = normalizeText(`${eleve.nom} ${eleve.prenom} ${eleve.classe_nom || ''} ${eleve.radiation_raison || ''}`);
      const matchesSearch = searchable.includes(normalizeText(searchTerm));
      const matchesReason = !reasonFilter || (eleve.radiation_raison || 'Non spécifiée') === reasonFilter;

      const date = new Date(eleve.date_radiation);
      const matchesPeriod = (() => {
        if (periodFilter === 'all') return true;
        if (Number.isNaN(date.getTime())) return false;
        if (periodFilter === 'month') {
          return date.getFullYear() === currentYear && date.getMonth() === currentMonth;
        }
        if (periodFilter === 'quarter') {
          return date.getFullYear() === currentYear && Math.floor(date.getMonth() / 3) === currentQuarter;
        }
        if (periodFilter === 'year') {
          return date.getFullYear() === currentYear;
        }
        return true;
      })();

      return matchesSearch && matchesReason && matchesPeriod;
    });
  }, [elevesRadies, periodFilter, reasonFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredEleves.length / ITEMS_PER_PAGE));
  const paginatedEleves = filteredEleves.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const selectedOnPage = paginatedEleves.length > 0 && paginatedEleves.every((eleve) => selectedIds.includes(eleve.id));

  useEffect(() => {
    setCurrentPage(1);
  }, [periodFilter, reasonFilter, searchTerm]);

  const toggleSelected = (id) => {
    setSelectedIds((previous) =>
      previous.includes(id) ? previous.filter((selectedId) => selectedId !== id) : [...previous, id]
    );
  };

  const toggleCurrentPageSelection = () => {
    const pageIds = paginatedEleves.map((eleve) => eleve.id);
    if (selectedOnPage) {
      setSelectedIds((previous) => previous.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((previous) => Array.from(new Set([...previous, ...pageIds])));
  };

  const openRestoreDecision = async (ids) => {
    if (!ids.length) return;

    if (ids.length > 1) {
      setConfirmRestore({ open: true, ids, mode: 'individuel' });
      return;
    }

    try {
      setRestorePreviewLoading(true);
      setRestorePreviewLoadingId(ids[0]);
      const response = await api.eleves.previewRestore(ids[0], {
        annee_scolaire_id: activeYear?.id
      });
      const preview = response?.data || response;

      setRestorePreview(preview);
      setRestoreTargetId(ids[0]);
      setRestoreMode(famillesOuvertes && preview?.has_family_history ? 'famille' : 'individuel');
      setRestoreTarifMode(preview?.options?.famille?.tarif_mode_recommande || 'dernier_etat');
    } catch (error) {
      console.error('Erreur préparation restauration:', error);
      showErrorToast("Erreur lors de la préparation de la restauration", { persistent: true });
    } finally {
      setRestorePreviewLoading(false);
      setRestorePreviewLoadingId(null);
    }
  };

  const closeRestoreDecision = () => {
    setRestorePreview(null);
    setRestoreTargetId(null);
    setRestoreMode('individuel');
    setRestoreTarifMode('dernier_etat');
  };

  const restoreStudents = async (ids, mode = 'individuel') => {
    if (!ids.length) return;

    try {
      setRestoring(true);
      showLoadingToast(ids.length > 1 ? 'Restauration des élèves en cours...' : "Restauration de l'élève en cours...");
      const payload = { mode, annee_scolaire_id: activeYear?.id };
      if (mode === 'famille') {
        payload.tarif_mode = restoreTarifMode;
      }
      const results = await Promise.allSettled(ids.map((id) => api.eleves.restore(id, payload)));

      // ⚠️ UNE PROMESSE TENUE N'EST PAS UNE RESTAURATION RÉUSSIE.
      //
      // `handleResponse` (services/api.js) NE LÈVE PAS sur 400, 403 et 409 : il
      // REND `{ success: false, message }`. Ce bloc comptait toute promesse
      // `fulfilled` comme une réussite, et comme le corps d'un refus ne porte pas
      // `validation_required`, le message tombait sur « Élève restauré avec
      // succès ». Autrement dit : le serveur refusait, et l'écran félicitait.
      //
      // Relevé le 05/10/2026 pendant la recette : une seconde demande de
      // restauration pour le même élève était refusée par la garde des doublons —
      // aucune ligne créée en base — et l'écran annonçait un succès.
      const tenues = results.filter((r) => r.status === 'fulfilled');
      const refusees = tenues.filter((r) => r.value?.success === false);
      const reussies = tenues.filter((r) => r.value?.success !== false);
      const rejetees = results.filter((r) => r.status === 'rejected');
      const restoredCount = reussies.length;

      // La restauration passe par le Centre de Support : tant que la demande n'est
      // pas approuvée, l'élève reste radié. Annoncer une restauration faite serait
      // faux, et la liste des radiés ne bougera pas au rechargement.
      const enAttenteValidation = reussies.some((r) => r.value?.validation_required);

      // Le repère se pose tout de suite sur les élèves dont la demande est partie :
      // c'est lui qui remplace la restauration que l'utilisateur s'attendait à voir.
      const posees = {};
      results.forEach((r, index) => {
        if (r.status !== 'fulfilled') return;
        if (r.value?.success === false || !r.value?.validation_required) return;
        posees[ids[index]] = { demande_id: r.value?.data?.demande_id || null, demande_le: null, demandeur: null };
      });
      if (Object.keys(posees).length > 0) {
        setRestaurationsEnAttente((prev) => ({ ...prev, ...posees }));
      }

      // ⚠️ UN SEUL MESSAGE, ET C'EST UN PRÉREQUIS, PAS UNE ÉLÉGANCE.
      //
      // Trois appels se succédaient ici — demandes envoyées, refus, échecs
      // techniques — et ils écrivent tous le MÊME état de toast. Comme ils sont
      // dans la même continuation après l'`await`, React 18 les regroupe en un
      // seul lot : UN SEUL était peint, le dernier. Sur une sélection mélangée,
      // le message que l'utilisateur voulait lire — sa demande partie au Centre
      // de Support — était justement celui qui perdait.
      //
      // On compose donc une seule phrase qui dit tout, et son traitement suit la
      // règle : un message qui contient un refus EST un refus, donc bandeau
      // persistant ; sinon une demande en attente mérite 8 secondes ; et une
      // restauration immédiate, visible à l'écran, garde les 3 secondes.
      const parties = [];
      if (restoredCount > 0) {
        if (enAttenteValidation) {
          parties.push(restoredCount > 1
            ? `${restoredCount} demandes de restauration envoyées au Centre de Support. Elles prendront effet après validation.`
            : 'Demande de restauration envoyée au Centre de Support. Elle prendra effet après validation.');
        } else {
          parties.push(restoredCount > 1 ? `${restoredCount} élèves restaurés.` : 'Élève restauré.');
        }
      }
      if (refusees.length > 0) {
        const motif = refusees[0].value?.message || 'voir le Centre de Support';
        parties.push(refusees.length === 1
          ? `Refusée : ${motif}`
          : `${refusees.length} refusées. Motif de la première : ${motif}`);
      }
      if (rejetees.length > 0) {
        parties.push(`${rejetees.length} restauration(s) en échec technique : rien n'a été fait.`);
      }

      setConfirmRestore({ open: false, ids: [] });
      closeRestoreDecision();

      // ⚠️ LE RECHARGEMENT PASSE AVANT LE MESSAGE. `loadElevesRadies` pose son
      // propre message si la lecture échoue, et il écrasait celui-ci — le
      // message sur l'action de l'utilisateur est le plus utile des deux, et la
      // liste montre déjà par elle-même que le rechargement a échoué.
      await loadElevesRadies();

      if (parties.length > 0) {
        const message = parties.join(' ');
        const estRefus = refusees.length > 0 || rejetees.length > 0;
        if (estRefus) {
          showErrorToast(message, { persistent: true });
        } else if (enAttenteValidation) {
          showSuccessToast(message, { duration: 8000 });
        } else {
          showSuccessToast(message);
        }
      }
    } catch (error) {
      console.error('Erreur restauration:', error);
      showErrorToast("Erreur lors de la restauration", { persistent: true });
    } finally {
      setRestoring(false);
    }
  };

  const previewEleve = restorePreview?.eleve || {};
  const previewFamily = famillesOuvertes ? (restorePreview?.options?.famille || null) : null;
  const previewIndividual = restorePreview?.options?.individuel || null;
  const selectedFamilyTarif = previewFamily?.tarifs?.[restoreTarifMode] || previewFamily;
  const selectedRestoreOption = restoreMode === 'famille'
    ? {
        ...previewFamily,
        part_mensuelle_a_remettre: selectedFamilyTarif?.part_mensuelle_a_remettre ?? previewFamily?.part_mensuelle_a_remettre,
        total_famille_apres_restauration: selectedFamilyTarif?.total_famille_apres_restauration ?? previewFamily?.total_famille_apres_restauration,
        formule_restauration: selectedFamilyTarif?.formule_restauration ?? previewFamily?.formule_restauration
      }
    : previewIndividual;
  const familyTarifOptions = [
    previewFamily?.tarifs?.dernier_etat,
    previewFamily?.tarifs?.frais_officiels
  ].filter(Boolean);

  return (
    <div className="w-full p-6">
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-[#246990]">Élèves radiés</h1>
        <p className="mt-1 text-sm text-gray-500">
          Liste d'attente des élèves sortis temporairement, avec restauration simple ou en masse.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Button
          onClick={() => window.history.back()}
          variant="outline"
          className="h-10 rounded-md border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:border-[#246990]/30 hover:bg-[#246990]/5 hover:text-[#246990]"
        >
          <ArrowLeft size={17} className="mr-2" />
          Retour à la liste des élèves
        </Button>

      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          label="Élèves radiés"
          value={stats.totalRadies}
          detail={`${stats.activeCount} actifs + ${stats.totalRadies} radiés = ${stats.effectifReference} suivis`}
          tone="blue"
        />
        <StatCard
          icon={TrendingDown}
          label="Taux de radiation"
          value={`${stats.tauxRadiation.toFixed(1)}%`}
          detail={`${stats.totalRadies} radiés / ${stats.effectifReference} élèves suivis`}
          tone="red"
        />
        <StatCard
          icon={Calendar}
          label="Ce trimestre"
          value={stats.currentQuarterCount}
          detail={`Ce mois : ${stats.currentMonthCount} - moyenne ${stats.monthlyAverage.toFixed(1)}/mois`}
          tone="teal"
        />
        <StatCard
          icon={AlertTriangle}
          label="Raison principale"
          value={stats.topReason ? `${stats.topReason.percentage.toFixed(0)}%` : '0%'}
          detail={stats.topReason ? `${stats.topReason.reason} (${stats.topReason.count})` : 'Aucune radiation'}
          tone="slate"
        />
      </div>

      <ProtectedComponent module="ELEVES" sousModule="RADIATION" action="VIEW_LIST">
        <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="font-bold text-gray-950">Lecture rapide</h2>
              <p className="mt-1 text-sm text-gray-500">Ce que les radiations racontent sur la période observée.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
              <div className="rounded-lg bg-[#246990]/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#246990]">Base de calcul</p>
                <p className="mt-2 text-2xl font-bold text-gray-950">{stats.effectifReference}</p>
                <p className="mt-1 text-sm text-gray-500">
                  {stats.activeCount} actifs + {stats.totalRadies} radiés
                </p>
              </div>
              <div className="rounded-lg bg-[#319190]/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#319190]">Pic observé</p>
                <p className="mt-2 text-lg font-bold text-gray-950">{getMonthLabel(stats.topMonth?.month)}</p>
                <p className="mt-1 text-sm text-gray-500">{stats.topMonth?.count || 0} radiation(s)</p>
              </div>
              <div className="rounded-lg bg-[#c66d59]/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#c66d59]">Risque principal</p>
                <p className="mt-2 text-lg font-bold text-gray-950">{stats.topReason?.reason || 'Aucune raison'}</p>
                <p className="mt-1 text-sm text-gray-500">
                  {stats.topReason ? `${stats.topReason.count} cas sur ${stats.totalRadies}` : 'Aucune donnée'}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="font-bold text-gray-950">Raisons les plus fréquentes</h2>
            </div>
            <div className="space-y-3 p-5">
              {stats.reasonStats.slice(0, 4).length === 0 ? (
                <p className="text-sm text-gray-500">Aucune radiation à analyser.</p>
              ) : (
                stats.reasonStats.slice(0, 4).map((item) => (
                  <div key={item.reason}>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-semibold text-gray-800">{item.reason}</span>
                      <span className="text-sm font-bold text-[#c66d59]">{item.count}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-[#c66d59]" style={{ width: `${item.percentage}%` }} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-bold text-gray-950">Liste des élèves radiés</h2>
                <p className="mt-1 text-sm text-gray-500">{filteredEleves.length} élève(s) affiché(s)</p>
              </div>

              <Button
                onClick={() => openRestoreDecision(selectedIds)}
                disabled={selectedIds.length === 0 || restoring || restorePreviewLoading}
                className="h-10 rounded-md bg-[#319190] px-4 text-sm font-semibold text-white hover:bg-[#287a79] disabled:opacity-50"
              >
                {restorePreviewLoading && selectedIds.length === 1 ? (
                  <Loader2 size={17} className="mr-2 animate-spin" />
                ) : (
                  <ArchiveRestore size={17} className="mr-2" />
                )}
                {restorePreviewLoading && selectedIds.length === 1
                  ? 'Préparation...'
                  : `Restaurer la sélection (${selectedIds.length})`}
              </Button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px_190px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Rechercher un élève, une classe ou une raison..."
                  className="h-10 w-full rounded-md border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
              <SimpleDropdown
                value={reasonFilter}
                onChange={setReasonFilter}
                options={reasonOptions}
                placeholder="Toutes les raisons"
              />
              <SimpleDropdown
                value={periodFilter}
                onChange={setPeriodFilter}
                options={[
                  { value: 'all', label: 'Toutes périodes' },
                  { value: 'month', label: 'Mois en cours' },
                  { value: 'quarter', label: 'Trimestre en cours' },
                  { value: 'year', label: 'Année civile en cours' }
                ]}
                placeholder="Toutes périodes"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#246990]">
              <Loader2 className="mr-3 animate-spin" size={24} />
              Chargement des élèves radiés...
            </div>
          ) : filteredEleves.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                <Users size={26} />
              </div>
              <p className="mt-4 font-semibold text-gray-900">Aucun élève radié</p>
              <p className="mt-1 text-sm text-gray-500">La liste se remplira automatiquement après une radiation.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1120px] text-left">
                  <thead className="border-b border-gray-100 bg-white text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="w-12 px-5 py-3">
                        <input
                          type="checkbox"
                          checked={selectedOnPage}
                          onChange={toggleCurrentPageSelection}
                          className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
                        />
                      </th>
                      <th className="px-5 py-3">Élève</th>
                      <th className="px-5 py-3">Classe</th>
                      <th className="px-5 py-3">Date et heure</th>
                      <th className="px-5 py-3">Raison</th>
                      <th className="px-5 py-3">Radié par</th>
                      <th className="px-5 py-3">Parent / Tuteur</th>
                      {/*
                        Une COLONNE pour la demande en cours, et non un badge collé au
                        nom. Placée juste avant « Action » : c'est là que l'œil va au
                        moment de décider s'il clique sur « Restaurer », et la colonne
                        du nom reste lisible.
                      */}
                      <th className="px-5 py-3">Demande en cours</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedEleves.map((eleve) => {
                      const isSelected = selectedIds.includes(eleve.id);
                      const isRestorePreviewLoading = restorePreviewLoading && restorePreviewLoadingId === eleve.id;
                      const initials = `${eleve.nom?.[0] || ''}${eleve.prenom?.[0] || ''}`.toUpperCase();
                      // ⚠️ UNE DEMANDE EN COURS FERME LA LIGNE À TOUTE NOUVELLE DEMANDE.
                      // Le serveur refuserait de toute façon — la garde des doublons est
                      // posée dans le service des demandes spéciales. Mais proposer un
                      // bouton dont on sait qu'il sera refusé, c'est faire travailler
                      // l'utilisateur pour rien, et c'est précisément ce qui lui a fait
                      // cliquer quatre fois. La case à cocher part avec : sans cela,
                      // « tout sélectionner » enverrait une demande vouée au refus pour
                      // chaque élève déjà en attente.
                      const demandeEnCours = restaurationsEnAttente[eleve.id] || null;

                      return (
                        <tr key={eleve.id} className={isSelected ? 'bg-[#246990]/5' : 'bg-white'}>
                          <td className="px-5 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={Boolean(demandeEnCours)}
                              onChange={() => toggleSelected(eleve.id)}
                              className="h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990] disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#246990] text-sm font-bold text-white">
                                {initials || 'EL'}
                              </div>
                              <div>
                                <p className="font-bold text-gray-950">{eleve.nom} {eleve.prenom}</p>
                                <p className="text-xs text-gray-500">Matricule : {eleve.matricule || 'Non renseigné'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="inline-flex rounded-md border border-[#246990]/15 bg-[#246990]/10 px-2 py-1 text-xs font-semibold text-[#246990]">
                              {eleve.classe_nom || 'Non renseignée'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-700">
                            <div className="font-semibold text-gray-800">{formatDate(eleve.date_radiation)}</div>
                            <div className="text-xs text-gray-500">
                              {formatTime(eleve.radiation_created_at, eleve.date_radiation)}
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="inline-flex max-w-[220px] rounded-md border border-red-100 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
                              <span className="truncate">{eleve.radiation_raison || 'Non spécifiée'}</span>
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-700">
                            {eleve.radiation_agent_nom || 'Non renseigné'}
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-700">
                            <div>{eleve.parent?.nom_prenom || eleve.parent_nom || 'Non renseigné'}</div>
                            {(eleve.parent?.telephone || eleve.parent_telephone) && (
                              <div className="text-xs text-gray-500">{eleve.parent?.telephone || eleve.parent_telephone}</div>
                            )}
                          </td>
                          {/*
                            LA COLONNE DE L'ATTENTE. Le badge était collé au nom,
                            dans la même cellule : il bousculait la ligne et se
                            lisait mal. Il a sa colonne, juste avant l'action, là
                            où l'œil va avant de cliquer.
                          */}
                          <td className="px-5 py-4">
                            {demandeEnCours ? (
                              <span className="inline-flex items-center whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                Restauration demandée
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <Button
                              onClick={() => openRestoreDecision([eleve.id])}
                              variant="outline"
                              disabled={restorePreviewLoading || restoring || Boolean(demandeEnCours)}
                              className="h-8 rounded-md border-[#319190]/25 bg-[#319190]/5 px-3 text-xs font-semibold text-[#287a79] hover:bg-[#319190]/10 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isRestorePreviewLoading ? (
                                <Loader2 size={14} className="mr-1.5 animate-spin" />
                              ) : (
                                <ArchiveRestore size={14} className="mr-1.5" />
                              )}
                              {isRestorePreviewLoading ? 'Préparation...' : demandeEnCours ? 'En attente' : 'Restaurer'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-4 text-sm text-gray-600">
                <span>
                  Page {currentPage} sur {totalPages} - {selectedIds.length} sélectionné(s)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="h-9 rounded-md border-gray-200"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9 rounded-md border-gray-200"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </ProtectedComponent>

      {restorePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
          <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="shrink-0 flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#246990]">Restauration</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-950">
                  {previewEleve.nom_complet || `${previewEleve.nom || ''} ${previewEleve.prenom || ''}`}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Classe : {previewEleve.classe_nom || 'Non renseignée'} · Matricule : {previewEleve.matricule || 'Non renseigné'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeRestoreDecision}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                disabled={restoring}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-4 text-sm text-[#1f5675]">
                {comptabiliteOuverte
                  ? "Les paiements déjà encaissés restent conservés. La restauration réactive uniquement les mois non verrouillés à partir du mois d'effet."
                  : "L'élève reprend sa place dans les effectifs. Son dossier et son historique de scolarité redeviennent actifs."}
              </div>

              <div className={`mt-5 grid gap-4 ${previewFamily ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                {previewFamily && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setRestoreMode('famille')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setRestoreMode('famille');
                    }}
                    className={`rounded-lg border p-4 text-left transition-colors ${restoreMode === 'famille'
                      ? 'border-[#246990] bg-[#246990]/10 ring-2 ring-[#246990]/15'
                      : 'border-gray-200 bg-white hover:border-[#246990]/35'
                      }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-950">Remettre dans sa famille</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {previewFamily.famille?.nom || 'Famille d’origine'}
                        </p>
                      </div>
                      <span className="rounded-md bg-[#246990]/10 px-2 py-1 text-xs font-bold text-[#246990]">
                        Famille
                      </span>
                    </div>
                    <div className="mt-4 grid gap-3 text-sm">
                      <div className="rounded-md bg-white p-3">
                        <span className="text-gray-500">Total actuel de la famille</span>
                        <div className="mt-1 font-bold text-gray-950">{formatMoney(previewFamily.total_famille_actuel)}</div>
                      </div>
                      <div className="grid gap-2">
                        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Tarif a restaurer</span>
                        {familyTarifOptions.map((option) => {
                          const isSelected = restoreTarifMode === option.mode;

                          return (
                            <button
                              key={option.mode}
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setRestoreMode('famille');
                                setRestoreTarifMode(option.mode);
                              }}
                              className={`rounded-md border bg-white p-3 text-left transition-colors ${isSelected
                                ? 'border-[#246990] ring-2 ring-[#246990]/10'
                                : 'border-gray-200 hover:border-[#246990]/30'
                                }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-bold text-gray-950">{option.label}</span>
                                    {option.recommande && (
                                      <span className="rounded-md bg-[#246990]/10 px-2 py-0.5 text-[11px] font-bold text-[#246990]">
                                        Recommande
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">{option.description}</p>
                                  {option.source?.label && (
                                    <p className="mt-1 text-[11px] font-semibold text-gray-500">{option.source.label}</p>
                                  )}
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-base font-bold text-gray-950">{formatMoney(option.part_mensuelle_a_remettre)}</p>
                                  <p className="mt-1 text-[11px] text-gray-500">part eleve</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="rounded-md bg-white p-3">
                        <span className="text-gray-500">Total famille après restauration</span>
                        <div className="mt-1 font-bold text-gray-950">{formatMoney(selectedRestoreOption?.total_famille_apres_restauration)}</div>
                        <div className="mt-1 text-xs text-gray-500">
                          {formatMoney(selectedRestoreOption?.formule_restauration?.total_actuel_restants)}
                          {' + '}
                          {formatMoney(selectedRestoreOption?.formule_restauration?.part_reintegree)}
                        </div>
                      </div>
                      <div className="rounded-md bg-amber-50 p-3 text-amber-800">
                        La famille est recomposée. Les mois payés ou partiels restent dans leur historique.
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setRestoreMode('individuel')}
                  className={`rounded-lg border p-4 text-left transition-colors ${restoreMode === 'individuel'
                    ? 'border-[#319190] bg-[#319190]/10 ring-2 ring-[#319190]/15'
                    : 'border-gray-200 bg-white hover:border-[#319190]/35'
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-950">
                        {comptabiliteOuverte ? 'Restaurer en élève seul' : "Réintégrer l'élève"}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {comptabiliteOuverte
                          ? 'Sans famille et sans réduction famille.'
                          : `Retour en ${previewEleve.classe_nom || 'sa classe'}.`}
                      </p>
                    </div>
                    {comptabiliteOuverte && (
                      <span className="rounded-md bg-[#319190]/10 px-2 py-1 text-xs font-bold text-[#287a79]">
                        Individuel
                      </span>
                    )}
                  </div>
                  <div className="mt-4 grid gap-3 text-sm">
                    {comptabiliteOuverte && (
                      <>
                        <div className="rounded-md bg-white p-3">
                          <span className="text-gray-500">Montant mensuel officiel</span>
                          <div className="mt-1 font-bold text-gray-950">{formatMoney(previewIndividual?.montant_mensuel)}</div>
                        </div>
                        <div className="rounded-md bg-white p-3">
                          <span className="text-gray-500">Réductions appliquées</span>
                          <div className="mt-1 font-bold text-gray-950">Aucune</div>
                        </div>
                      </>
                    )}
                    <div className="rounded-md bg-gray-50 p-3 text-gray-700">
                      {comptabiliteOuverte
                        ? "La famille reste dans son état après radiation. L’élève reprend les frais officiels de sa classe."
                        : "Les notes et l’historique déjà enregistrés sont conservés."}
                    </div>
                  </div>
                </button>
              </div>

              {/* Mode, mois d'effet et mois a reactiver decrivent la recomposition
                  de l'attendu. Sans comptabilite il n'y a ni mois d'effet ni mode :
                  un seul chemin, et rien a recomposer. */}
              {comptabiliteOuverte && (
              <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Mode choisi</p>
                    <p className="mt-1 font-bold text-gray-950">
                      {restoreMode === 'famille' ? 'Restauration dans la famille' : 'Restauration en élève seul'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Mois d'effet</p>
                    <p className="mt-1 font-bold text-gray-950">
                      {selectedRestoreOption?.mois_effet?.nom || 'Non déterminé'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Mois concernés</p>
                    <p className="mt-1 font-bold text-gray-950">
                      {(selectedRestoreOption?.mois_a_reactiver?.length || 0) > 0
                        ? `${selectedRestoreOption.mois_a_reactiver.length} mois à réactiver`
                        : 'Selon recomposition famille'}
                    </p>
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className="shrink-0 flex items-center justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4">
              <Button
                variant="outline"
                onClick={closeRestoreDecision}
                disabled={restoring}
                className="h-10 rounded-md border-gray-200 px-4"
              >
                Annuler
              </Button>
              <Button
                onClick={() => restoreStudents([restoreTargetId], restoreMode)}
                disabled={!restoreTargetId || restoring}
                className="h-10 rounded-md bg-[#246990] px-4 font-semibold text-white hover:bg-[#1f5b7d]"
              >
                {restoring ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <ArchiveRestore size={16} className="mr-2" />
                )}
                {restoring ? 'Restauration...' : 'Confirmer la restauration'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmRestore.open}
        onClose={() => setConfirmRestore({ open: false, ids: [] })}
        onConfirm={() => restoreStudents(confirmRestore.ids, confirmRestore.mode || 'individuel')}
        title="Confirmer la restauration"
        description={
          confirmRestore.ids.length > 1
            ? `Voulez-vous restaurer ${confirmRestore.ids.length} élèves en élèves seuls ? Les familles éventuelles ne seront pas recomposées en masse.`
            : "Voulez-vous restaurer cet élève dans la liste active ?"
        }
        confirmText={confirmRestore.ids.length > 1 ? 'Restaurer les élèves' : "Restaurer l'élève"}
        cancelText="Annuler"
        type="question"
        isLoading={restoring}
        confirmDisabled={confirmRestore.ids.length === 0}
      />

      {/*
        ⚠️ L'ARBITRAGE, ET NON `<Toast>` EN DUR.
        Cet écran rendait le toast court directement : il ne pouvait donc PAS
        honorer une option `long`, le composant court n'ayant ni `options`, ni
        `title`, ni `long`. Et le seuil automatique des 130 caractères ne le
        sauvait pas — les messages de ce module mesurent entre 44 et 104
        caractères, donc tous condamnés aux 3 secondes du toast court.
        Même motif que FamillesNombreuses et EmployeesList, déjà en place.
      */}
      {showToast && (
        shouldUseLongToast(toastMessage, toastType, toastOptions) ? (
          <LongToast
            show
            title={toastOptions.title}
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        ) : (
          <Toast
            duration={toastOptions.duration}
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )
      )}
    </div>
  );
};

export default ElevesRadies;
